/**
 * Toast and SweetAlert2 wrapper helpers.
 *
 * `Swal` and `Toast` are window globals registered by
 * `src/Services/GlobalVariables.ts`; their contracts are described in
 * `src/types/window.d.ts`. We reference them via `window.*` so the
 * typecheck contract tracks the augmented Window interface.
 *
 * Each method returns the awaited Swal result. Using
 * `Awaited<ReturnType<typeof Swal.fire>>` keeps the signature tied to the
 * actual SweetAlert2 typings (no hand-rolled result shape).
 */
export const responses = {
  /**
   * Shows a blocking error dialog with the given message HTML.
   *
   * @param err - Error payload rendered inside a `<pre>` block.
   * @param title - Dialog title (default `An Error Occurred`).
   * @returns Promise resolving to the SweetAlert2 fire result.
   */
  throwErr(err: unknown, title: string = 'An Error Occurred'): Promise<Awaited<ReturnType<typeof window.Swal.fire>>> {
    return window.Swal.fire({
      title: title,
      icon: 'error',
      html: `<pre>${String(err)}</pre>`,
      showCloseButton: true,
    });
  },

  /**
   * Shows a success toast notification.
   *
   * @param msg - Toast title text.
   * @returns Promise resolving to the Toast fire result.
   */
  showSuccess(msg: string): Promise<Awaited<ReturnType<typeof window.Swal.fire>>> {
    return window.Toast.fire({
      icon: 'success',
      title: msg,
    });
  },

  /**
   * Shows an informational toast notification.
   *
   * @param msg - Toast title text.
   * @returns Promise resolving to the Toast fire result.
   */
  showInfo(msg: string): Promise<Awaited<ReturnType<typeof window.Swal.fire>>> {
    return window.Toast.fire({
      icon: 'info',
      title: msg,
    });
  },

  /**
   * Shows a warning toast notification.
   *
   * @param msg - Toast title text.
   * @returns Promise resolving to the Toast fire result.
   */
  showWarning(msg: string): Promise<Awaited<ReturnType<typeof window.Swal.fire>>> {
    return window.Toast.fire({
      icon: 'warning',
      title: msg,
    });
  },
};